<script setup lang="ts">
import { computed } from "vue";

import ListGroup from "@/components/ui/ComplexList/ListGroup.vue";
import ListGroupHeader from "@/components/ui/ComplexList/ListGroupHeader.vue";
import ListHeaderContent from "@/components/ui/ComplexList/ListHeaderContent.vue";
import ScreenReaderMessage from "@/components/ui/a11y/ScreenReaderMessage.vue";
import { boardTypeName } from "@/model/baseTypes";
import type { SearchResultKey } from "@/model/search";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";

import GroupedByTypeSearchResults from "./GroupedByTypeSearchResults.vue";
import type { CardsOfBoard } from "./groupSearchResults";

const emit = defineEmits<{ select: [item: SearchResultKey] }>();
const props = defineProps<{ results: CardsOfBoard; selected?: SearchResultKey }>();

const currentBoardId = computed(() => useBoardStore().currentBoard().id);

const board = computed(() => useBoardsStore().boardById(props.results.boardId));
</script>

<template>
  <ListGroup class="current-board-search-results">
    <!-- Results count (screen reader only) -->
    <li>
      <ScreenReaderMessage>
        {{ $t("label.resultsCount.currentBoard", results.cards.length) }}
      </ScreenReaderMessage>
    </li>

    <!-- Current board title -->
    <template #header>
      <ListGroupHeader class="current-board-title">
        <ListHeaderContent :text="board.type === 'team' ? board.team.name : results.boardName" />
        <ListHeaderContent v-if="board.type === 'team'" sub :text="$t(boardTypeName(board.type))" />
        <template #after>
          <div class="items-count">
            {{ $t("searchResultsTs.stickies", results.cards.length) }}
          </div>
        </template>
      </ListGroupHeader>
    </template>

    <!-- Results -->
    <GroupedByTypeSearchResults
      :items="results.cards"
      :board-id="currentBoardId"
      :selected
      @select="emit('select', $event)"
    />
  </ListGroup>
</template>

<style lang="scss" scoped>
@use "@/styles/font";
@use "@/styles/variables/colors";

.current-board-title {
  margin-top: 4px;

  .items-count {
    font-weight: font.$weight-medium;
    font-size: font.$size-small;
  }
}

.divider {
  border-top: 1px solid colors.$border-subtle;
  width: 100%;
}
</style>
